import type { Map as MapLibreMap } from "maplibre-gl";

import { closeRing, type Ring } from "../data/site-boundary";

function ringToSvgPoints(map: MapLibreMap, ring: Ring): string {
  const closed = closeRing(ring);
  return closed
    .map(([lng, lat]) => {
      const projected = map.project([lng, lat]);
      return `${projected.x},${projected.y}`;
    })
    .join(" ");
}

export function appendPolygon(
  svg: SVGSVGElement,
  ring: Ring,
  map: MapLibreMap,
  fill: string,
  stroke: string,
  strokeWidth: number,
): void {
  if (ring.length < 3) {
    return;
  }
  const polygon = document.createElementNS("http://www.w3.org/2000/svg", "polygon");
  polygon.setAttribute("points", ringToSvgPoints(map, ring));
  polygon.setAttribute("fill", fill);
  polygon.setAttribute("stroke", stroke);
  polygon.setAttribute("stroke-width", String(strokeWidth));
  polygon.setAttribute("stroke-linejoin", "round");
  svg.appendChild(polygon);
}

export function appendPolyline(
  svg: SVGSVGElement,
  ring: Ring,
  map: MapLibreMap,
  stroke: string,
  strokeWidth: number,
  dashed: boolean,
): void {
  if (ring.length < 2) {
    return;
  }
  const polyline = document.createElementNS("http://www.w3.org/2000/svg", "polyline");
  polyline.setAttribute(
    "points",
    ring
      .map(([lng, lat]) => {
        const projected = map.project([lng, lat]);
        return `${projected.x},${projected.y}`;
      })
      .join(" "),
  );
  polyline.setAttribute("fill", "none");
  polyline.setAttribute("stroke", stroke);
  polyline.setAttribute("stroke-width", String(strokeWidth));
  polyline.setAttribute("stroke-linejoin", "round");
  polyline.setAttribute("stroke-linecap", "round");
  if (dashed) {
    polyline.setAttribute("stroke-dasharray", "8 4");
  }
  svg.appendChild(polyline);
}

/**
 * Viewport-stable map pin. Used on MapTiler hybrid where GeoJSON circle layers can sit under late
 * raster/style layers even when raise/reposition runs, so the SVG plane is the honest operator
 * paint for fixed posts (same pattern as boundary/geofence fills).
 */
export function appendMapPin(
  svg: SVGSVGElement,
  lng: number,
  lat: number,
  map: MapLibreMap,
  fill: string,
  stroke: string,
  radiusPx = 9,
  label?: string,
): void {
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return;
  }
  const projected = map.project([lng, lat]);
  const group = document.createElementNS("http://www.w3.org/2000/svg", "g");
  group.setAttribute("transform", `translate(${projected.x} ${projected.y})`);

  const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
  circle.setAttribute("cx", "0");
  circle.setAttribute("cy", "0");
  circle.setAttribute("r", String(radiusPx));
  circle.setAttribute("fill", fill);
  circle.setAttribute("stroke", stroke);
  circle.setAttribute("stroke-width", "3");
  circle.setAttribute("opacity", "0.95");
  group.appendChild(circle);

  if (label !== undefined && label.trim().length > 0) {
    const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
    text.setAttribute("x", "0");
    text.setAttribute("y", String(-(radiusPx + 6)));
    text.setAttribute("text-anchor", "middle");
    text.setAttribute("fill", "#f8fafc");
    text.setAttribute("stroke", "rgba(7, 11, 19, 0.85)");
    text.setAttribute("stroke-width", "3");
    text.setAttribute("paint-order", "stroke");
    text.setAttribute("font-size", "11");
    text.setAttribute("font-weight", "700");
    text.setAttribute("font-family", "system-ui, sans-serif");
    text.textContent = label.trim().slice(0, 40);
    group.appendChild(text);
  }

  svg.appendChild(group);
}
